"use client";
import {
  Box,
  Typography,
  Grid,
  Container,
  IconButton,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  YouTube,
  Phone,
  Email,
  LocationOn,
  Store,
  People,
  Inventory,
  GetApp,
  Security,
  LocalShipping,
  Support,
} from "@mui/icons-material";
import logo1 from "../../assets/images/logo1.png";
import playstore from "../playstore.png";
import appstore from "../appstore.png";

const StatsSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const stats = [
    {
      icon: <Store sx={{ fontSize: 40, color: "#008080" }} />,
      number: "100+",
      label: "Pharma Companies",
      description: "Trusted Partners",
    },
    {
      icon: <Inventory sx={{ fontSize: 40, color: "#008080" }} />,
      number: "5000+",
      label: "Product Range",
      description: "Quality Medicines",
    },
    {
      icon: <People sx={{ fontSize: 40, color: "#008080" }} />,
      number: "500+",
      label: "Happy Retailers",
      description: "Satisfied Customers",
    },
  ];

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
        py: 6,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} justifyContent="center">
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: 3,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 12px 24px rgba(0,128,128,0.15)",
                  },
                }}
              >
                <Box sx={{ mb: 2 }}>{stat.icon}</Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "bold",
                    color: "#008080",
                    mb: 1,
                  }}
                >
                  {stat.number}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#2c3e50",
                    mb: 0.5,
                  }}
                >
                  {stat.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const CompanySection = () => {
  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #008080 0%, #20b2aa 100%)",
        py: 6,
        color: "white",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={3} textAlign="center">
            <Box
              component="img"
              src={logo1}
              alt="EvaraLifeSciences.com"
              sx={{
                width: { xs: 80, md: 120 },
                height: "auto",
                filter: "brightness(0) invert(1)",
                mb: { xs: 2, md: 0 },
              }}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                textAlign: { xs: "center", md: "left" },
              }}
            >
              Leading Pharmaceutical Distribution
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.7,
                opacity: 0.9,
                textAlign: { xs: "center", md: "left" },
              }}
            >
              EvaraLifeSciences.com is the leading wholesale distributor of
              pharmaceutical products, serving healthcare providers, pharmacies,
              hospitals, and clinics across India with quality medicines and
              exceptional service.
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const MainFooter = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const footerSections = [
    {
      title: "Customer Service",
      icon: <Support />,
      links: [
        {
          text: "Contact Us",
          href: "/support",
          icon: <Phone sx={{ fontSize: 16 }} />,
        },
        {
          text: "Returns & Refunds",
          href: "#",
          icon: <Security sx={{ fontSize: 16 }} />,
        },
        {
          text: "Shipping Information",
          href: "#",
          icon: <LocalShipping sx={{ fontSize: 16 }} />,
        },
        { text: "FAQs", href: "#", icon: <Support sx={{ fontSize: 16 }} /> },
      ],
    },
    {
      title: "Company",
      icon: <Store />,
      links: [
        { text: "Our Story", href: "#" },
        { text: "Careers", href: "#" },
        { text: "Privacy Policy", href: "#" },
        { text: "Terms of Service", href: "#" },
      ],
    },
    {
      title: "Connect With Us",
      icon: <People />,
      links: [],
    },
  ];

  const socialLinks = [
    { icon: <Facebook />, href: "#", label: "Facebook", color: "#1877f2" },
    { icon: <Twitter />, href: "#", label: "Twitter", color: "#1da1f2" },
    { icon: <Instagram />, href: "#", label: "Instagram", color: "#e4405f" },
    { icon: <LinkedIn />, href: "#", label: "LinkedIn", color: "#0077b5" },
    { icon: <YouTube />, href: "#", label: "YouTube", color: "#ff0000" },
  ];

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
        color: "white",
        py: 6,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {footerSections.map((section, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  {section.icon}
                  <Typography
                    variant="h6"
                    sx={{
                      ml: 1,
                      fontWeight: 600,
                      color: "#00d4aa",
                    }}
                  >
                    {section.title}
                  </Typography>
                </Box>

                {section.title === "Connect With Us" ? (
                  <Box>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}
                    >
                      {socialLinks.map((social, idx) => (
                        <IconButton
                          key={idx}
                          href={social.href}
                          sx={{
                            color: "white",
                            backgroundColor: "rgba(255,255,255,0.1)",
                            "&:hover": {
                              backgroundColor: social.color,
                              transform: "translateY(-2px)",
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          {social.icon}
                        </IconButton>
                      ))}
                    </Box>

                    <Box sx={{ mt: 3 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 2, color: "#00d4aa" }}
                      >
                        Contact Information
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Phone sx={{ fontSize: 16, mr: 1, color: "#00d4aa" }} />
                        <Typography variant="body2">+91 9289451500</Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Email sx={{ fontSize: 16, mr: 1, color: "#00d4aa" }} />
                        <Typography variant="body2">
                          evaralifesciences@gmail.com
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        <LocationOn
                          sx={{
                            fontSize: 16,
                            mr: 1,
                            mt: 0.5,
                            color: "#00d4aa",
                          }}
                        />
                        <Typography variant="body2">
                          Delhi, India
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    {section.links.map((link, linkIndex) => (
                      <Box
                        key={linkIndex}
                        component="a"
                        href={link.href}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: "rgba(255,255,255,0.8)",
                          textDecoration: "none",
                          mb: 1.5,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            color: "#00d4aa",
                            transform: "translateX(5px)",
                          },
                        }}
                      >
                        {link.icon && (
                          <Box sx={{ mr: 1, color: "#00d4aa" }}>
                            {link.icon}
                          </Box>
                        )}
                        <Typography variant="body2">{link.text}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 4, backgroundColor: "rgba(255,255,255,0.1)" }} />

        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              color: "#00d4aa",
              fontWeight: 600,
            }}
          >
            <GetApp sx={{ mr: 1, verticalAlign: "middle" }} />
            Download Our Mobile App
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box
              component="a"
              href="https://play.google.com/store/apps/details?id=com.namami.evara_app"
              sx={{
                display: "block",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            >
              <Box
                component="img"
                src={playstore}
                alt="Google Play"
                sx={{
                  width: { xs: 120, sm: 140 },
                  height: "auto",
                  borderRadius: 2,
                }}
              />
            </Box>
            <Box
              component="a"
              href="https://apps.apple.com/us/app/evara/id6736901161"
              sx={{
                display: "block",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            >
              <Box
                component="img"
                src={appstore}
                alt="App Store"
                sx={{
                  width: { xs: 120, sm: 140 },
                  height: "auto",
                  borderRadius: 2,
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            textAlign: "center",
            pt: 3,
            borderTop: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255,255,255,0.7)",
              mb: 2,
            }}
          >
            © {new Date().getFullYear()} EVARA LIFE SCIENCES. All Rights
            Reserved.
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Chip
              label="Trusted Partner"
              size="small"
              sx={{
                backgroundColor: "rgba(0,212,170,0.2)",
                color: "#00d4aa",
                border: "1px solid rgba(0,212,170,0.3)",
              }}
            />
            <Chip
              label="Quality Assured"
              size="small"
              sx={{
                backgroundColor: "rgba(0,212,170,0.2)",
                color: "#00d4aa",
                border: "1px solid rgba(0,212,170,0.3)",
              }}
            />
            <Chip
              label="Pan India Delivery"
              size="small"
              sx={{
                backgroundColor: "rgba(0,212,170,0.2)",
                color: "#00d4aa",
                border: "1px solid rgba(0,212,170,0.3)",
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const Footer = () => {
  return (
    <Box component="footer" sx={{ mt: "auto" }}>
      <StatsSection />
      <CompanySection />
      <MainFooter />
    </Box>
  );
};

export default Footer;
